import { useQuery } from '@apollo/client';
import type { Zone } from '@seek/audience-zones';
import { useHubble } from '@seek/hubble';
import type { Language } from '@seek/melways-sites';

import { GetRecommendationsDocument } from 'src/graphql/graphql';
import { selectSmarterSearchCluster } from 'src/modules/featureFlag';

import { hasAuthError } from './errorHandler';
import { pluckRecommendedGlobalJobsData, pluckUserData } from './helper';
import { useLocale } from './useLocale';
import { useTimezone } from './useTimezone';

const useRecommendations = ({
  language: languageCode,
  zone,
}: {
  language: Language;
  zone: Zone;
}) => {
  const locale = useLocale();
  const hubble = useHubble();
  const visitorId = hubble.visitorId();
  const cluster = selectSmarterSearchCluster();
  const timezone = useTimezone();
  const { loading, data, error } = useQuery(GetRecommendationsDocument, {
    ssr: false,
    variables: {
      cluster,
      languageCode,
      locale,
      visitorId,
      zone,
      timezone,
    },
  });

  const userData = pluckUserData(data);
  const { solMetadata, globalJobs, jobCount } =
    pluckRecommendedGlobalJobsData(data);
  const hasError = Boolean(error) || !globalJobs || jobCount === undefined;

  return {
    error,
    hasAuthError: hasAuthError(error),
    hasError,
    loading,
    userData,
    solMetadata,
    globalJobs: globalJobs || [],
    jobCount: jobCount || 0,
  };
};

export default useRecommendations;
